/**
 * What a film the trainer offers becomes before it is sent, when it is refused, and what a refusal
 * from the backend reads as. Pure: no DOM, no worker, no i18next — keys, never sentences.
 *
 * ── One pipeline for every film (owner decision, 25 September 2026) ──────────────────────────
 * Validate, compress in the browser, check the 50 MiB delivery cap, then slot → presigned PUT →
 * confirm. The same order the phone runs (`useVideoPickCompress`), so a film the web sends is the
 * film the phone would have sent, and a raw 4K clip never reaches R2 or the server's ffmpeg.
 *
 * `lib`-shaped work — decoding, encoding, the worker — is `prepareVideo` and
 * `compressVideo.worker`; the decisions they make are here, where a test can reach them.
 *
 * ── A film the browser cannot convert is not automatically refused ──────────────────────────
 * WebCodecs support differs by browser and by codec (an iPhone HEVC clip, AAC encoding on Firefox).
 * When the browser cannot produce the MP4 itself, the ORIGINAL goes instead, but only when the
 * backend would take it as it stands: already `video/mp4` and within the cap. The server's own
 * transcode then does the work. Anything else is refused with the reason, never sent to fail.
 *
 * ── A browser's limit is refused as the browser's, whatever else is wrong with the film ─────
 * When the browser could not convert and the original cannot go, the trainer is told the browser
 * is the obstacle, even when the original is also over 50 MiB: „za duży” would send them looking
 * for a smaller file that this browser still could not read. Which half failed is kept apart —
 * the film's picture cannot be DECODED here (its codec is carried, so an iPhone's HEVC is named),
 * or it was read and could not be ENCODED — because the first is about the file and the second is
 * not, and the sentences say different things.
 */

import type { VideoCodec } from 'mediabunny';

import { ApiError } from '~/shared/api/mutator';

import { longEdgeScale, reencodedFileName } from '../reencodedFile';

import { VIDEO_CLASS } from './videoClass';

/** What the file chooser offers: any film, since every one is re-encoded when the browser can. */
export const VIDEO_ACCEPT = 'video/*';

/**
 * Why a film cannot be sent.
 *
 * · `notAVideo` — the browser does not call it a film at all.
 * · `undecodable` — it says it is one and nothing in it can be read (a broken file, a container the
 *   browser does not know).
 * · `tooLong` — longer than the slot allows, in the whole seconds the slot counts.
 * · `tooLarge` — over the 1.5 GiB sanity gate raw, or over 50 MiB as it would be sent.
 * · `unsupportedBrowser` — this browser cannot make an MP4 of it (it could not encode what it
 *   read, or has no worker to try), and the original is not one the backend would take as it
 *   stands.
 * · `{ unreadableCodec }` — the same, except that this browser cannot even decode the film's
 *   picture; the codec the container names (`hevc` for an iPhone clip), or `null` when it names
 *   none this reader knows.
 */
export type VideoRefusal =
  | 'notAVideo'
  | 'undecodable'
  | 'tooLong'
  | 'tooLarge'
  | 'unsupportedBrowser'
  | { readonly unreadableCodec: VideoCodec | null };

/** The refusal a file earns BEFORE anything is decoded, or `null` when it is worth reading. */
export function rawVideoRefusal(file: {
  readonly type: string;
  readonly size: number;
}): VideoRefusal | null {
  if (!file.type.toLowerCase().startsWith('video/')) return 'notAVideo';
  return file.size > VIDEO_CLASS.preflightMaxBytes ? 'tooLarge' : null;
}

/**
 * The duration the slot is asked for: whole seconds, never below one.
 *
 * The slot refuses `durationSeconds <= 0` (`media_duration_exceeded`), and a clip shorter than half
 * a second rounds to zero — the phone's chat and clip paths sent exactly that. The worker re-derives
 * the real duration while processing, so a floored one only has to keep the slot request valid.
 */
export function slotDurationSeconds(seconds: number): number {
  return Math.max(1, Math.round(seconds));
}

/**
 * The refusal a film's duration earns, or `null`.
 *
 * Judged on the value the SLOT will be sent, so the browser and the backend cannot disagree about a
 * film of 120.4 seconds: it rounds to 120 and passes both; 120.5 rounds to 121 and is refused here,
 * before it is compressed, rather than there after it was uploaded.
 */
export function durationRefusal(seconds: number): VideoRefusal | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'undecodable';
  return slotDurationSeconds(seconds) > VIDEO_CLASS.maxDurationSeconds ? 'tooLong' : null;
}

/**
 * The size a film is encoded at: the longest edge at most `maxEdge`, the proportions kept, never
 * larger than it already is — and both sides even, which H.264's 4:2:0 sampling requires.
 */
export function videoTargetSize(
  width: number,
  height: number,
): { readonly width: number; readonly height: number } {
  const scale = longEdgeScale(width, height, VIDEO_CLASS.maxEdge);
  const even = (side: number) => Math.max(2, 2 * Math.floor((side * scale) / 2));
  return { width: even(width), height: even(height) };
}

/**
 * Which file is sent — the browser's own encode or the trainer's original — or why neither can be.
 *
 * The encode is preferred, unless the original is already an MP4 the backend takes and the encode
 * came out no smaller (a film already at 720p and a low bitrate gains nothing from a second pass,
 * and loses quality to it). `encode` is the browser's refusal when it could not convert at all; the
 * original is then the only candidate, and only if it is an MP4 within the cap — otherwise that
 * refusal is the answer, never „too large” (see the header). The 50 MiB cap applies to what would
 * actually be sent.
 */
export function deliveryOf(
  original: { readonly type: string; readonly size: number },
  encode: { readonly size: number } | { readonly refusal: VideoRefusal },
): { readonly send: 'converted' | 'original' } | { readonly refusal: VideoRefusal } {
  const originalSendable =
    original.type.toLowerCase() === VIDEO_CLASS.contentType &&
    original.size <= VIDEO_CLASS.maxDeliveryBytes;
  if ('refusal' in encode) return originalSendable ? { send: 'original' } : encode;
  if (originalSendable && encode.size >= original.size) return { send: 'original' };
  return encode.size > VIDEO_CLASS.maxDeliveryBytes ? { refusal: 'tooLarge' } : { send: 'converted' };
}

/** The encoded file's name: the trainer's own, with the extension the bytes now have. */
export function videoFileName(original: string): string {
  return reencodedFileName(original, 'film', 'mp4');
}

/**
 * The sentence a refusal in the zone reads as, as a key.
 *
 * The browser's two refusals end in one shared paragraph (`videoBrowserLimit.help`, nested in the
 * copy): what the app takes, which browsers do it best, and where to write when it keeps failing.
 */
export function videoRefusalKey(refusal: VideoRefusal): string {
  if (typeof refusal === 'object') {
    return refusal.unreadableCodec === 'hevc'
      ? 'web:media.videoBrowserLimit.cannotReadHevc'
      : 'web:media.videoBrowserLimit.cannotRead';
  }
  switch (refusal) {
    case 'notAVideo':
      return 'web:media.videoNotAVideo';
    case 'undecodable':
      return 'web:media.videoUndecodable';
    case 'unsupportedBrowser':
      return 'web:media.videoBrowserLimit.cannotPrepare';
    case 'tooLong':
      return 'shared.media.tooLong';
    case 'tooLarge':
      return 'shared.media.tooLarge';
  }
}

/** The sentence an upload that did not go through reads as, when nothing more specific is known. */
export const VIDEO_UPLOAD_FAILED_KEY = 'shared.media.videoUploadFailed';

/**
 * The sentence a failed upload reads as, as a key.
 *
 * The backend says WHY on the slot (Problem Details `code`), and three of its reasons are not worth
 * trying again: the same film will be refused the same way. Saying „spróbuj ponownie” there sends
 * the trainer into a loop. A full slot table (`media_slot_limit`, 409) and a rate limit (429) are
 * the opposite, and say so. Anything else — the PUT, the confirm, a dead connection — is the
 * generic sentence.
 *
 * The reasons are SHORT and carry no instruction (`web:media.videoNotSent.*`): an upload fails after
 * the save, so the reason is read inside a sentence that already says where to add the film
 * again, and a second instruction beside it („Wybierz…”, „Poczekaj i spróbuj ponownie”) asked the
 * trainer to do something on a wizard that had closed.
 */
export function videoFailureKey(error: unknown): string {
  if (!(error instanceof ApiError)) return VIDEO_UPLOAD_FAILED_KEY;
  switch (error.code) {
    case 'media_too_large':
      return 'web:media.videoNotSent.tooLarge';
    case 'media_duration_exceeded':
      return 'shared.media.tooLong';
    case 'media_unsupported_type':
      return 'web:media.videoNotSent.unsupportedType';
    case 'media_slot_limit':
      return 'web:media.videoNotSent.slotLimit';
    default:
      return error.status === 429 ? 'web:media.videoNotSent.slotLimit' : VIDEO_UPLOAD_FAILED_KEY;
  }
}

/**
 * Where a sent film stands, from `GET /media/{id}/status`: `ready` and `failed` are the end of it,
 * anything else (`Pending`, `Confirmed`, `Processing`) means ask again. Read without regard to case,
 * as the phone reads it.
 */
export function mediaProcessingOutcome(
  status: string | null | undefined,
): 'ready' | 'failed' | null {
  const read = status?.toLowerCase();
  return read === 'ready' || read === 'failed' ? read : null;
}

/** How long to wait before asking again, after `answers` answers: the phone's 2 s doubling to 15 s. */
export function processingPollDelayMs(answers: number): number {
  return Math.min(2_000 * 2 ** answers, 15_000);
}

/** Why an aborted preparation or upload stopped, as an Error — the signal's own when it is one. */
export function abortReason(signal: AbortSignal | undefined): Error {
  const reason: unknown = signal?.reason;
  return reason instanceof Error ? reason : new DOMException('The film was cancelled', 'AbortError');
}
